import {type Instruction} from '@backstage/instructions';
import {globalVariable, localVariable, storage} from '../../helpers';
import {JSONObject, SiteVariableLookup} from '../../types';
import {getFlows, triggerTransformation} from './transformations/converter';
import {BroadcastFunction} from './transformations/node.types';
import type {AppPage} from './types';

interface ProcessInstructionsArgs {
  /** Domain on which the `showId` was viewed */
  domainName?: string;
  /** Function to execute to get details about the current page */
  getCurrentPage: () => AppPage | undefined;
  /** The batch of instructions to process */
  instructions: Instruction[];
  /** Id of the show whose instructions are being handled. */
  showId: string;
  /** Function to broadcast a new instruction into the broker */
  broadcast: BroadcastFunction;
  /** "dictionary" of SiteVariable keys to values */
  siteVariableLookup: SiteVariableLookup;
}

/**
 * Transforms instructions as needed for reactivity (e.g. `variable:set` to
 * `variable:on-set`, `CustomInstruction:broadcast` to
 * `CustomInstruction:on-broadcast`) and then triggers the flow transformations
 * for each instruction.
 */
export function processInstructionFlows(args: ProcessInstructionsArgs): void {
  const {
    domainName,
    getCurrentPage,
    instructions,
    showId,
    broadcast,
    siteVariableLookup,
  } = args;
  const flows = getFlows(domainName);
  const actions = preprocessInstructionBatch(instructions);
  const structure = getCurrentPage()?.structure;
  // Process all the variables manipulations BEFORE allowing flows to react to
  // variable setting (or unsetting). It is necessary they are all processed
  // before flows which may be reacting to those instructions are executed in
  // order to ensure consistent flow behavior.
  let changedGlobalState: Record<string, string> = {};
  actions.forEach((inst) => {
    if (inst.type === 'Global:variable:on-set') {
      setVariables(inst.meta, globalVariable(showId));
      changedGlobalState = Object.assign(changedGlobalState, inst.meta);
    } else if (inst.type === 'variable:on-set') {
      setVariables(inst.meta, localVariable(showId));
    }
  });
  // If global state has been modified broadcast an instruction indicating the
  // global state that changed
  if (Object.keys(changedGlobalState).length > 0) {
    broadcast(
      {type: 'Global:state:on-change', meta: changedGlobalState},
      'internal'
    );
  }
  // process the flows associated with instructions in the batch
  actions.forEach((inst) => {
    const flowStartNodes = flows[inst.type];
    if (structure && Array.isArray(flowStartNodes)) {
      flowStartNodes.forEach((nodeMapper) => {
        triggerTransformation({
          nodeMapper,
          inst,
          broadcast,
          showId,
          structure,
          siteVariableLookup,
        });
      });
    }
  });
}

/**
 * Transforms the `type` of `Instruction` to match `NodeType.listener` naming
 * convention.
 * - `variable:set` becomes `variable:on-set`
 * - `variable:unset` becomes `variable:on-unset`
 * - `CustomInstruction:broadcast` becomes `CustomInstruction:on-broadcast`
 * @private exported for tests
 */
export function preprocessInstructionBatch(
  instructions: Instruction[]
): Instruction[] {
  return instructions.map((action) => {
    if (action.type === 'CustomInstruction:broadcast') {
      return {
        ...action,
        type: 'CustomInstruction:on-broadcast',
      };
    } else if (
      action.type.endsWith('variable:set') ||
      action.type.endsWith('variable:unset')
    ) {
      return {
        ...action,
        type: action.type.replace(/^(.*variable):((un)?set)/, '$1:on-set'),
      };
    } else {
      return action;
    }
  });
}

/** For a given object, modify `storage` for each key value pair */
const setVariables = (variables: JSONObject, prefix: string): void => {
  Object.entries(variables).forEach(([key, value]) => {
    if (value === null) {
      storage.removeItem(`${prefix}${key}`);
    } else {
      storage.setItem(`${prefix}${key}`, JSON.stringify(value));
    }
  });
};
